import { Fragment, useEffect, useRef, useState } from "react";

const PROGRESSION = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "scheduled", label: "Scheduled" },
  { key: "converted", label: "Converted" },
];

// Closing a lead is rare and near-final, so the three ways to do it live
// behind one menu instead of three buttons with the same weight as the
// progression. `category` actions set lead_category, not status.
const CLOSE_ACTIONS = [
  { key: "noshow", label: "No Show", type: "status", dot: "bg-rose-400" },
  { key: "lost", label: "Not Interested", type: "status", dot: "bg-slate-400" },
  {
    key: "not_applicable",
    label: "Not Applicable",
    type: "category",
    dot: "bg-gray-300",
  },
];

const Icon = ({ d, className = "h-3.5 w-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const PATH = {
  new: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  contacted:
    "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  scheduled:
    "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  converted: "M5 13l4 4L19 7",
  check: "M5 13l4 4L19 7",
  chevronDown: "M19 9l-7 7-7-7",
};

/**
 * Where the lead stands, and the one control that moves it.
 *
 * The progression is a stepper, not four buttons: it has to say what is done
 * and what is next, which a row of equal buttons never did. It keeps a single
 * accent — the per-status colours (amber, violet, emerald…) earn their place
 * in the list and the sticky header, where you scan many leads at once, but
 * inside one card they only add hues.
 *
 * Every step stays clickable. Staff set the wrong status often enough that
 * walking it back has to be one click.
 */
const LeadStatusFlow = ({
  currentStatus = "new",
  currentCategory = "prospect",
  onStatusChange,
  onCategoryChange,
  onScheduleAppointment,
  size = "default",
  // Mobile view: no appointment or category changes, so drop the Scheduled step
  // and the Not Applicable (category) close action.
  statusOnly = false,
}) => {
  const large = size === "large";
  const [showCloseMenu, setShowCloseMenu] = useState(false);
  const closeMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (closeMenuRef.current && !closeMenuRef.current.contains(e.target)) {
        setShowCloseMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const steps = statusOnly ? PROGRESSION.filter((s) => s.key !== "scheduled") : PROGRESSION;
  const closeActions = statusOnly ? CLOSE_ACTIONS.filter((a) => a.type === "status") : CLOSE_ACTIONS;
  const status = currentStatus?.toLowerCase();
  const category = currentCategory?.toLowerCase();
  const currentIndex = steps.findIndex((s) => s.key === status);
  const closedBy = CLOSE_ACTIONS.find((a) =>
    a.type === "status" ? a.key === status : a.key === category,
  );

  const handleStepClick = (key) => {
    onStatusChange?.(key);
    if (key === "scheduled" && onScheduleAppointment) {
      onScheduleAppointment();
    }
  };

  const handleCloseAction = (action) => {
    setShowCloseMenu(false);
    if (action.type === "status") {
      onStatusChange?.(action.key);
    } else {
      onCategoryChange?.(action.key);
    }
  };

  return (
    <div
      className={
        large
          ? "flex items-start gap-x-4"
          : "flex flex-wrap items-center gap-x-4 gap-y-2"
      }
    >
      {/* A closed lead has left the progression, so the steps go quiet and the
          state is spelled out in the menu label beside them. */}
      <div
        className={`flex items-start ${large ? "min-w-0 flex-1" : "w-64"} ${
          closedBy ? "opacity-50" : ""
        }`}
      >
        {steps.map((step, i) => {
          const done = currentIndex > -1 && i < currentIndex;
          const current = i === currentIndex;
          return (
            <Fragment key={step.key}>
              {i > 0 && (
                <div
                  className={`${large ? "mt-4" : "mt-2.5"} h-px flex-1 ${
                    done || current ? "bg-teal-600" : "bg-border"
                  }`}
                />
              )}
              <button
                data-attr="lead-status-change"
                onClick={() => handleStepClick(step.key)}
                aria-current={current ? "step" : undefined}
                title={`Mark as ${step.label}`}
                aria-label={`Mark as ${step.label}`}
                className={`group flex ${
                  large ? "w-20" : "w-14"
                } flex-col items-center gap-0.5 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
              >
                <span
                  className={`flex ${
                    large ? "h-8 w-8" : "h-5 w-5"
                  } items-center justify-center rounded-full border transition-colors ${
                    done
                      ? "border-teal-600 bg-teal-600 text-white"
                      : current
                      ? "border-teal-600 bg-teal-600 text-white ring-2 ring-teal-100"
                      : "border-border bg-white text-slate-400 group-hover:border-teal-400 group-hover:text-teal-600"
                  }`}
                >
                  <Icon
                    d={done ? PATH.check : PATH[step.key]}
                    className={large ? "h-4 w-4" : "h-3 w-3"}
                  />
                </span>
                <span
                  className={`${
                    large ? "text-sm" : "text-[10px]"
                  } leading-tight ${
                    current
                      ? "font-semibold text-teal-800"
                      : done
                      ? "font-medium text-text-primary"
                      : "text-text-secondary"
                  }`}
                >
                  {step.label}
                </span>
              </button>
            </Fragment>
          );
        })}
      </div>

      <div>
        <div className="relative inline-block" ref={closeMenuRef}>
          <button
            onClick={() => setShowCloseMenu((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={showCloseMenu}
            className={`inline-flex items-center gap-1.5 text-text-secondary transition-colors hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              large
                ? "rounded-md border border-border bg-white px-3 py-2 text-sm"
                : "rounded px-1 py-0.5 text-xs"
            }`}
          >
            {closedBy ? (
              <>
                <span className={`h-2 w-2 rounded-full ${closedBy.dot}`} />
                <span className="font-medium text-text-primary">
                  Closed · {closedBy.label}
                </span>
              </>
            ) : (
              "Close lead"
            )}
            <Icon d={PATH.chevronDown} className="h-3.5 w-3.5 opacity-60" />
          </button>

          {showCloseMenu && (
            <div
              role="menu"
              className="absolute left-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-white py-1 shadow-lg"
            >
              {closeActions.map((action) => (
                <button
                  key={action.key}
                  role="menuitem"
                  data-attr="lead-status-change"
                  onClick={() => handleCloseAction(action)}
                  className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition-colors hover:bg-gray-50"
                >
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${action.dot}`} />
                    <span className="font-medium text-text-primary">
                      {action.label}
                    </span>
                  </span>
                  {closedBy?.key === action.key && (
                    <Icon d={PATH.check} className="h-4 w-4 text-teal-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadStatusFlow;
